import { useAtomValue } from "jotai/index";
import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { accountAtom } from "shared/lib/account";
import { getAbsoluteUrl } from "shared/lib/utils";

import { useHomeStoryPackageTopic } from "scmp-app/components/home/story-package/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { secondaryStoryPackageQuery$key } from "scmp-app/queries/__generated__/secondaryStoryPackageQuery.graphql";

import { Container, StyledTopStoriesBlock, TopicBadge } from "./styles";

type Props = {
  className?: string;
  reference: secondaryStoryPackageQuery$key;
};

export const HomeSecondaryStoryPackage: FunctionComponent<Props> = ({
  className,
  reference: reference_,
}) => {
  const data = useFragment(
    graphql`
      fragment secondaryStoryPackageQuery on Query
      @argumentDefinitions(secondaryStoryPackageArticlesQueueName: { type: "String!" }) {
        secondaryStoryPackageArticles: queue(
          filter: { name: $secondaryStoryPackageArticlesQueueName }
        ) {
          ...hooksHomeStoryPackageTopicQueue
          items(first: 4) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...topStoriesBlockQueueItemsEdge
            }
          }
        }
      }
    `,
    reference_,
  );

  const user = useAtomValue(accountAtom);
  const secondaryStoryPackageItems = data.secondaryStoryPackageArticles?.items?.edges ?? [];
  const listOfArticles = (
    data?.secondaryStoryPackageArticles?.items?.edges?.map(item => item.node.entityId) ?? []
  ).join(",");

  const storyPackageTopic = useHomeStoryPackageTopic(
    data?.secondaryStoryPackageArticles ?? undefined,
  );

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        block_section: storyPackageTopic.name,
        list_of_articles: listOfArticles,
        page_type: "homepage",
        widget_name: "feature_package_2",
      },
      subcategory: "widget",
    }),
    [listOfArticles, storyPackageTopic],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.StoryPackageTwo,
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <TopicBadge>{storyPackageTopic.component}</TopicBadge>

        <StyledTopStoriesBlock
          isReadingTimeEnabled={true}
          onClick={(entityId, urlAlias) => {
            sendGA4Tracking({
              action: "click",
              category: "recirculation",
              customized_parameters: {
                article_id: entityId,
                block_section: storyPackageTopic.name,
                destination_url: getAbsoluteUrl(urlAlias),
                page_type: "homepage",
                widget_name: "feature_package_2",
              },
              subcategory: "widget",
            });
          }}
          reference={secondaryStoryPackageItems}
          withBookmark={user.isLoggedIn}
          withCaption={false}
          withCarousel={true}
        />
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeSecondaryStoryPackage.displayName = "HomeSecondaryStoryPackage";
